import {
  createTheme,
  PaletteOptions,
  responsiveFontSizes,
  BreakpointsOptions,
} from '@mui/material/styles';
import Color from 'color';
import { useThemeMode } from '../../hooks/theme';
import { useBreakpointSm } from '../../utils/useStyleHooks';

const spacingUnit = 8;
const fontFamily = 'Inter, sans-serif, Arial';

/**
 * Rewrite MUI's built-in color style
 * Usage: theme.palette.primary.main ...
 */

const fnThemeConfig = (mode: 'light' | 'dark') => {
  const M = (l: string, d?: string) => (mode === 'light' ? l : d || l);

  return {
    palette: {
      mode,
      background:{
        default: M('#FFFFFF', '#000000'),
      },
      primary: {
        main: M('#8135F2'),
        nav_checked: M('#8135F2','#C2AFFF'),
        nav_default: M('#5D5D5D', '#E7E7E7'),
        '2': M('#11111180', '#FFFFFF80'),
        '2_2': M('#F0EDFD80', '#342F46'),
        '3': M('#6D6D6D', '#888888'),
        '4': M('#111111', '#FFFFFF'),
        '4_a': M('#FFFFFF','#111111'),
        '5': M('#000000', '#FFFFFF'),
        '5_a': M('#FFFFFF', '#000000'),
        'bg_1': M("#F6F6F6",'#272727'),
        '25_25': M('#F0EDFD', '#F0EDFD'),
      },
      grey: {
        white_600: M('#fff', '#302C44'),
        '25': M('#E7E7E7', '#454545'),
        '26': M('#FFFFFF', '#2B2B2B'),
        '27': M('#F4F4F4', '#B0B0B0'),
        '28': M('#888888', '#B0B0B0'),
        '29': M('#D0D5DD', '#404040'),
        '30': M('#FFFFFF', '#404040'),
        '31': M('#111111', '#F6F6F6'),
        '32': M('#D1D1D1', '#6D6D6D'),
        '33': M('rgba(0,0,0,0.1)', 'rgba(255,255,255,0.1)'),
        '34': M('#5D5D5D', '#B0B0B0'),
        '35': M('#00000080', '#FFFFFF33'),
        '36': M('#0000004A', '#FFFFFF66'),
        '37': M('#454545', '#E6E6E6'),
        '38': M('#F2F4F7', '#454545'),
        '50_2': M('#645E7D', '#fff'),
        '700': M('#111111', '#EBEAF0'),
        disabled_bg: M('rgba(0,0,0,0.12)', 'rgba(255,255,255,0.12)'),
        'txt_1': M('#4F4F4F', '#B0B0B0'),
        'txt_2': M('#888888', '#B0B0B0'),
      },
      neutral: {
        '700': M('#DDDBE6', '#50496E'),
      },
      danger: {
        '100': M('#FFF2F0'),
        '300': M('#FEB8AE'),
        '400': M('#FD5E49'),
        '500': M('#F15146'),
        '600': M('#F04438'),
        '700': M('#E33D72'),
      },
      text: {
        black_while: M('#000000', '#fff'),
        disabled: M('#EBEAF0', '#191528'),
      },
      warning: {
        '25': M('#FBFAF8'),
        '50': M('#FFF8EB'),
        '100': M('#FFE4C0'),
        '500': M('#FFAE43'),
        '600': M('#EEA23E'),
        '700': M('#D78100'),
      },
      success: {
        main: M('#41AE49'),
        '50': M('#F0FAF0'),
        '100': M('#C9EBCB'),
        '500': M('#5DC264'),
        '600': M('#41AE49'),
        '700': M('#2D8A39'),
        '800': M('#27682C'),
        '900': M('#1A451D'),
      },
      info: {
        main: M('#8135F2', '#fff'),
      },
      action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hoverOpacity: 0.04,
        selected: 'rgba(0, 0, 0, 0.08)',
        selectedOpacity: 0.08,
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(6,3,21,0.5)',
        disabledOpacity: 0.38,
        focus: 'rgba(0, 0, 0, 0.12)',
        focusOpacity: 0.12,
        activatedOpacity: 0.12,
        bgPrimary: 'rgba(248, 182, 255, 0.2)',
      },
    },
    breakpoints: {
      keys: ['xs', 'sm', 'md', 'lg', 'xl'],
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1080,
        xl: 1440,
        homepage: 1440,
      },
      unit: 'px',
    },
    typography: { fontFamily, htmlFontSize: 100 },
    spacing: spacingUnit,
    shape: { borderRadius: 0 },
    zIndex: { modal: 1000 },
    shadow: { sxm: '0px 1px 2px ' + M(`rgba(25, 21, 40, 0.04)`) },
    custom: {
      primary5: M('#F9F8FE', '#3E3956'),
      btn_1: M('#F5F3FF', '#F5F3FF33'),
      btn_2: M('transparent', '#F5F3FF33'),
      btn_3: M('rgba(0,0,0,0.25)', 'rgba(255,255,255,0.2)'),
      btn_txt: M('#8135F2', '#DDD5FF'),
      txt_1: M('#7123DE', '#DDD5FF'),
      txt_2: M('#667085', '#F5F3FF'),
      txt_3: M('#8135F2', '#A888FD'),
      txt_4: M('#36363C', '#CACAC4'),
      txt_5: M('#111111', '#E7E7E7'),
      bd_1: M('#F6F6F6', '#FFFFFF0D'),
      bd_2: M('#F6F6F6', '#454545'),
      bd_3: M('#F6F6F6', '#000000'),
      ck_1: M('#EEE8FF', '#494352'),
      hover_1: M('#E0DAFF', '#444444'),
      load_1: M('rgba(255,255,255,0.5)', 'rgba(0,0,0,0.5)'),
      shadow_1: M('0px 0px 5px 0px #00000040', 'none'),
      check_1: M('#E5DFFF', '#47425E'),
      dis_1: M('#E9D7FE', '#594E66'),
      tas_1: M('#7e57c2','#8135F2'),
      bg_1: M('#FFFFFF','#202020'),
      bg_2: M("#B0B0B0",'#4F4F4F')
    },
  };
};

const themeConfigForModule = fnThemeConfig('dark');

declare module '@mui/material/styles' {
  export interface Theme {
    palette: typeof themeConfigForModule.palette;
    shadow: typeof themeConfigForModule.shadow;
    custom: typeof themeConfigForModule.custom;
  }
  interface ThemeOptions {
    palett?: typeof themeConfigForModule.palette;
    shadow?: typeof themeConfigForModule.shadow;
    custom?: typeof themeConfigForModule.custom;
  }
}

function useTheme() {
  const themeMode = useThemeMode();
  const isSM = useBreakpointSm();
  const themeConfig = fnThemeConfig(isSM ? "light" : themeMode);
  const isDark = themeMode === 'dark' && window.innerWidth > 460
  let theme = createTheme({
    palette: themeConfig.palette as PaletteOptions,
    breakpoints: themeConfig.breakpoints as BreakpointsOptions,
    typography: themeConfig.typography,
    spacing: themeConfig.spacing,
    shape: themeConfig.shape,
    shadow: themeConfig.shadow,
    custom: themeConfig.custom,
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme)=>({
          '::-webkit-scrollbar-track': {
            background: isDark ? '#333' : '#ECEFF0',
          },
          '::-webkit-scrollbar-thumb': {
            background: isDark ? '#888' : '#D5DADA',
          },
          '::-webkit-scrollbar-thumb:hover': {
            background: isDark ? '#aaa' : '#ECEFF0',
          },
        })
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            '& .MuiTableRow-root:hover': {
              backgroundColor: `${themeConfig.palette.warning[25]} !important`,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.14rem',
            color: themeConfig.palette.primary['5'],
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: { padding: '0 !important' },
        },
      },
      // MuiMenu: {
      //   styleOverrides: { root: { '&>.MuiPaper-root': {}, }, },
      // },
      MuiList: {
        styleOverrides: {
          root: {
            '&>li': {
              borderLeft: `0.01rem solid ${themeConfig.palette.neutral['700']}`,
              borderRight: `0.01rem solid ${themeConfig.palette.neutral['700']}`,
            },
            '&>li:last-of-type': {
              borderBottom: `0.01rem solid ${themeConfig.palette.neutral['700']}`,
              borderBottomLeftRadius: '0.1rem',
              borderBottomRightRadius: '0.1rem',
              borderTop: '0 !important',
            },
            '&>li:first-of-type': {
              borderTop: `0.01rem solid ${themeConfig.palette.neutral['700']}`,
              borderTopLeftRadius: '0.1rem',
              borderTopRightRadius: '0.1rem',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontWeight: 400,
            fontSize: '0.14rem',
            color: themeConfig.palette.grey[700],
            height: '0.4rem',
            backgroundColor: themeConfig.palette.grey["26"],
            // '&.Mui-selected': {
            //   backgroundColor: `${themeConfig.custom.primary5} !important`,
            // },
            // '&.Mui-focusVisible': {
            //   backgroundColor: `${themeConfig.custom.primary5} !important`,
            // },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          popper: {
            boxShadow: `0px 1px 2px rgba(25, 21, 40, 0.04)`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: `4px`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            '&.Mui-disabled': {
              opacity: '1',
              filter: 'grayscale(30%)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '0.14rem',
            boxShadow: 'none',
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor: Color(themeConfig.palette.primary.main)
                .fade(0.1)
                .toString(),
            },
            '&.Mui-disabled': {
              backgroundColor: themeConfig.palette.grey.disabled_bg,
              opacity: '1',
              filter: 'grayscale(30%)',
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontWeight: 400,
            fontSize: '15px',
            color: themeConfig.palette.grey['50_2'],
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme);
  return theme;
}

export { useTheme };
